import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@rekati/utils";

const layoutSpring = { type: "spring" as const, bounce: 0.12, damping: 34, stiffness: 420 };

export interface SegmentedItem {
  value: string;
  label: ReactNode;
  /** A keyboard hint shown on the segment (Y / N); purely visual. */
  hint?: string;
}

/**
 * Cult UI's Halo Segmented, rebuilt as a radiogroup (no Base UI) and restyled
 * flat: a muted pill track and a yellow thumb that slides to the chosen
 * segment. Arrow keys move and choose, as a radiogroup should.
 */
export function HaloSegmented({
  items,
  value,
  onValueChange,
  labelledBy,
  describedBy,
  invalid,
  size = "md",
  className,
}: {
  items: SegmentedItem[];
  value: string;
  onValueChange: (v: string) => void;
  labelledBy?: string;
  describedBy?: string | undefined;
  invalid?: boolean;
  size?: "md" | "lg";
  className?: string;
}) {
  const reduce = useReducedMotion();
  const track = useRef<HTMLDivElement>(null);
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const [thumb, setThumb] = useState({ x: 0, width: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const t = track.current;
      const el = refs.current.get(value);
      if (!t || !el) return setThumb({ x: 0, width: 0 });
      const tr = t.getBoundingClientRect();
      const br = el.getBoundingClientRect();
      setThumb({ x: br.left - tr.left, width: br.width });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (track.current) ro.observe(track.current);
    return () => ro.disconnect();
  }, [value, items]);

  const move = (e: KeyboardEvent, i: number) => {
    const dir =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (!dir) return;
    e.preventDefault();
    const next = items[(i + dir + items.length) % items.length];
    if (!next) return;
    onValueChange(next.value);
    refs.current.get(next.value)?.focus();
  };

  return (
    <div
      className={cn(
        "inline-flex max-w-full rounded-full bg-muted p-1",
        invalid && "ring-2 ring-destructive",
        className,
      )}
    >
      <div
        ref={track}
        role="radiogroup"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className="relative flex min-w-0 flex-1"
      >
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 rounded-full bg-volt"
          initial={false}
          animate={{ x: thumb.x, width: thumb.width, opacity: thumb.width ? 1 : 0 }}
          transition={reduce ? { duration: 0 } : layoutSpring}
        />
        {items.map((item, i) => {
          const on = item.value === value;
          return (
            <button
              key={item.value}
              ref={(el) => {
                if (el) refs.current.set(item.value, el);
                else refs.current.delete(item.value);
              }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on || (!value && i === 0) ? 0 : -1}
              onClick={() => onValueChange(item.value)}
              onKeyDown={(e) => move(e, i)}
              className={cn(
                "relative z-10 flex flex-1 items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-colors",
                size === "lg" ? "min-h-14 px-4 text-lg sm:px-8" : "min-h-10 px-4 text-sm sm:px-5",
                on ? "text-volt-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {item.hint ? (
                <span
                  aria-hidden="true"
                  className="grid size-6 place-items-center rounded-md border border-current/40 font-mono text-xs"
                >
                  {item.hint}
                </span>
              ) : null}
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
